import React, {useEffect, useState} from 'react';
import '../styles/Tutorials.css';
import DashBoardNavBar from "./DashboardNavbar";
import { useNavigate } from "react-router-dom";
import firebase from "../firebaseConfig";
import useProfilePicture from "../hooks/useProfilePicture";
import { FaStar, FaArrowRight } from 'react-icons/fa';
import { BsGraphUp, BsPersonBadge } from 'react-icons/bs';

// Inline styles for the feedback page
const styles = {
  feedbackCard: {
    padding: '30px',
    borderRadius: '8px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    height: '100%',
    background: '#fff',
    transition: 'all 0.3s ease',
  },
  sectionHeader: {
    marginBottom: '25px',
    borderBottom: '1px solid #eee',
    paddingBottom: '15px',
  },
  featureIcons: {
    display: 'flex',
    justifyContent: 'space-around',
    marginTop: '30px',
  },
  featureItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
  },
  icon: {
    fontSize: '1.8rem',
    marginBottom: '10px',
    color: '#4285f4',
  },
  testimonialStars: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: '20px',
  },
  star: {
    color: '#FFD700',
    fontSize: '1.8rem',
    margin: '0 3px',
  },
  infoText: {
    padding: '15px',
    background: 'rgba(66, 133, 244, 0.05)',
    borderRadius: '6px',
    borderLeft: '4px solid #4285f4',
    fontSize: '0.9rem',
  }
};

const Feedback = ({ user }) => {
    const [userName, setUserName] = useState('');
    const [userId, setUserId] = useState('');
    const [title, setTitle] = useState('');
    const [description, setDescription] = useState('');
    const [publicReview, setPublicReview] = useState('');
    const [existingReviewId, setExistingReviewId] = useState(null);
    const [technicalFeedbackSubmitted, setTechnicalFeedbackSubmitted] = useState(false);
    const [testimonialSubmitted, setTestimonialSubmitted] = useState(false);
    const [hasPublicReview, setHasPublicReview] = useState(false);
    const [isEditingReview, setIsEditingReview] = useState(false);
    const [userSocialLink, setUserSocialLink] = useState({ type: '', url: '' });
    
    // Utiliser le hook personnalisé pour la photo de profil
    const { profilePicture: userProfilePicture } = useProfilePicture(user?.uid);
    const navigate = useNavigate();
    useEffect(() => {
        // Vérifier si l'utilisateur dispose d'un abonnement valide et récupérer son avis public s'il existe
        const checkPlanValidity = async () => {
            if (user) {
                try {
                    const userRef = firebase.firestore().collection('users').doc(user.uid);
                    const userDoc = await userRef.get();
                    if (userDoc.exists) {
                        setUserName(userDoc.data().userName || user.email);
                        setUserSocialLink(userDoc.data().socialLink || { type: '', url: '' });
                    } else {
                        setUserName(user.email);
                        setUserSocialLink({ type: '', url: '' });
                    }
                    setUserId(user.uid);

                    // Vérifier si l'utilisateur a déjà soumis un avis public
                    const reviewsRef = firebase.firestore().collection('publicReviews');
                    const querySnapshot = await reviewsRef.where('userId', '==', user.uid).get();
                    
                    if (!querySnapshot.empty) {
                        setHasPublicReview(true);
                        // Récupérer l'avis existant pour permettre la modification
                        const reviewDoc = querySnapshot.docs[0];
                        setExistingReviewId(reviewDoc.id);
                        setPublicReview(reviewDoc.data().review);
                        // Synchroniser le doc d'avis avec les dernières infos de profil
                        try {
                            const latestName = (userDoc.exists ? (userDoc.data().userName || user.email) : user.email);
                            const latestLink = (userDoc.exists ? (userDoc.data().socialLink || null) : null);
                            await firebase.firestore().collection('publicReviews').doc(reviewDoc.id).update({
                                userName: latestName,
                                profilePicture: userProfilePicture,
                                ...(latestLink && latestLink.url
                                    ? { socialLink: latestLink }
                                    : { socialLink: firebase.firestore.FieldValue.delete() })
                            });
                        } catch (e) {
                            // non bloquant
                        }
                    }
                } catch (error) {
                    console.error("Error retrieving user data:", error);
                }
            } else {
                navigate('/login');
            }
        };
        
        checkPlanValidity();
    }, []);

    // Function to handle technical feedback submission
    const handleTechnicalFeedbackSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate('/login');
            return;
        }
        
        if (!title.trim() || !description.trim()) {
            return; // Don't submit if required fields are empty
        }
        
        try {
            await firebase.firestore().collection('feedbacks').add({
                userId: user.uid,
                title: title,
                description: description,
                timestamp: firebase.firestore.FieldValue.serverTimestamp()
            });

            setTitle('');
            setDescription('');
            setTechnicalFeedbackSubmitted(true);
            setTimeout(() => setTechnicalFeedbackSubmitted(false), 5000);
        } catch (error) {
            console.error("Error submitting technical feedback:", error);
        }
    };
    
    // Function to handle public testimonial submission or update
    const handleTestimonialSubmit = async (e) => {
        e.preventDefault();
        if (!user) {
            navigate('/login');
            return;
        }
        
        if (publicReview.trim() === '') {
            return; // Don't submit if field is empty
        }
        
        try {
            if (existingReviewId && isEditingReview) {
                // Update existing review
                await firebase.firestore().collection('publicReviews').doc(existingReviewId).update({
                    review: publicReview,
                    userName: userName,
                    profilePicture: userProfilePicture || '',
                    ...(userSocialLink && userSocialLink.url
                        ? { socialLink: userSocialLink }
                        : { socialLink: firebase.firestore.FieldValue.delete() }),
                    updatedAt: firebase.firestore.FieldValue.serverTimestamp()
                });
                setIsEditingReview(false);
            } else if (!hasPublicReview) {
                // Create new review
                const docRef = await firebase.firestore().collection('publicReviews').add({
                    userId: user.uid,
                    userName: userName,
                    profilePicture: userProfilePicture || '',
                    ...(userSocialLink && userSocialLink.url ? { socialLink: userSocialLink } : {}),
                    review: publicReview,
                    timestamp: firebase.firestore.FieldValue.serverTimestamp()
                });
                setExistingReviewId(docRef.id);
                setHasPublicReview(true);
            }
            
            setTestimonialSubmitted(true);
            setTimeout(() => setTestimonialSubmitted(false), 5000);
        } catch (error) {
            console.error("Error submitting testimonial:", error);
        }
    };
    
    // Function to enable editing mode
    const enableEditMode = () => {
        setIsEditingReview(true);
    };

    return (
        <>
            <DashBoardNavBar userName={userName} boldItem={"feedback"} />
            <div className="container">
                <section className="section-header mt-5 mb-5">
                    <div className="text-center">
                        <h1>REPORT A BUG / GIVE FEEDBACK</h1>
                        <p className="lead">Help us improve your experience with OFD</p>
                    </div>
                </section>
                
                {(technicalFeedbackSubmitted || testimonialSubmitted) && (
                    <div className="alert alert-success alert-dismissible fade show mb-4" role="alert">
                        <strong>Thank you!</strong> Your submission has been received successfully.
                        <button type="button" className="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                    </div>
                )}
                
                <div className="row g-4 mb-5">
                    <div className="col-lg-6">
                        <form onSubmit={handleTechnicalFeedbackSubmit}>
                            <div style={styles.feedbackCard}>
                                <div style={styles.sectionHeader}>
                                    <h2>Technical Support</h2>
                                    <p>Report bugs or suggest improvements for our team</p>
                                </div>
                                
                                <div className="mb-4">
                                    <label htmlFor="title" className="form-label">Feedback Title</label>
                                    <input 
                                        type="text" 
                                        className="form-control" 
                                        id="title" 
                                        value={title} 
                                        onChange={(e) => setTitle(e.target.value)} 
                                        placeholder="E.g.: Issue with registration form"
                                        required 
                                    />
                                </div>
                                
                                <div className="mb-4">
                                    <label htmlFor="description" className="form-label">Detailed Description</label>
                                    <textarea 
                                        className="form-control" 
                                        id="description" 
                                        rows="5" 
                                        value={description} 
                                        onChange={(e) => setDescription(e.target.value)} 
                                        placeholder="Please describe the issue in detail. Include steps to reproduce the bug if possible."
                                        required 
                                    />
                                </div>
                                
                                <div className="text-center mt-4">
                                    <button type="submit" className="btn btn-danger px-4 py-2">
                                        Submit Technical Feedback <FaArrowRight style={{marginLeft: '8px'}} />
                                    </button>
                                </div>
                                
                                <div style={styles.featureIcons}>
                                    <div style={styles.featureItem}>
                                        <BsGraphUp style={styles.icon} />
                                        <span>Helps us improve</span>
                                    </div>
                                    <div style={styles.featureItem}>
                                        <BsPersonBadge style={styles.icon} />
                                        <span>Direct to our team</span>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    
                    <div className="col-lg-6">
                        <form onSubmit={handleTestimonialSubmit}>
                            <div style={styles.feedbackCard}>
                                <div style={styles.sectionHeader}>
                                    <h2>Public Testimonial</h2>
                                    <p>Share your experience with other users</p>
                                </div>
                                
                                <div className="text-center mb-4">
                                    <div style={styles.testimonialStars}>
                                        <FaStar style={styles.star} />
                                        <FaStar style={styles.star} />
                                        <FaStar style={styles.star} />
                                        <FaStar style={styles.star} />
                                        <FaStar style={styles.star} />
                                    </div>
                                </div>
                                
                                <div className="mb-4">
                                    <label htmlFor="publicReview" className="form-label">Your Testimonial</label>
                                    <textarea 
                                        className="form-control" 
                                        id="publicReview" 
                                        rows="5" 
                                        value={publicReview} 
                                        onChange={(e) => setPublicReview(e.target.value)}
                                        placeholder="Share your experience with OFD..." 
                                        disabled={hasPublicReview && !isEditingReview}
                                        required
                                    />
                                    
                                    {hasPublicReview && !isEditingReview ? (
                                        <div className="d-flex justify-content-between align-items-center mt-3">
                                            <div className="alert alert-info mb-0">
                                                <i className="fas fa-info-circle me-2"></i>
                                                Your testimonial is visible on the homepage
                                            </div>
                                            <button 
                                                type="button" 
                                                onClick={enableEditMode} 
                                                className="btn btn-outline-secondary"
                                            >
                                                Edit Testimonial
                                            </button>
                                        </div>
                                    ) : (
                                        <p style={styles.infoText}>
                                            Your testimonial will appear on our homepage along with your username to help other users discover OFD.
                                        </p>
                                    )}
                                </div>
                                
                                <div className="text-center mt-4">
                                    <button 
                                        type="submit" 
                                        className="btn btn-primary px-4 py-2" 
                                        disabled={hasPublicReview && !isEditingReview}
                                    >
                                        {isEditingReview ? (
                                            <>Update Testimonial <FaArrowRight style={{marginLeft: '8px'}} /></>
                                        ) : (
                                            <>Submit Testimonial <FaStar style={{marginLeft: '8px'}} /></>
                                        )}
                                    </button>
                                    
                                    {isEditingReview && (
                                        <button 
                                            type="button" 
                                            className="btn btn-outline-secondary px-4 py-2 ms-2" 
                                            onClick={() => {
                                                setIsEditingReview(false);
                                                // Reset to original value from Firestore
                                                const checkPlanValidity = async () => {
                                                    if (existingReviewId) {
                                                        const reviewDoc = await firebase.firestore()
                                                            .collection('publicReviews')
                                                            .doc(existingReviewId)
                                                            .get();
                                                        
                                                        if (reviewDoc.exists) {
                                                            setPublicReview(reviewDoc.data().review);
                                                        }
                                                    }
                                                };
                                                checkPlanValidity();
                                            }}
                                        >
                                            Cancel
                                        </button>
                                    )}
                                </div>
                                
                                <div style={styles.featureIcons}>
                                    <div style={styles.featureItem}>
                                        <FaStar style={styles.icon} />
                                        <span>Featured on homepage</span>
                                    </div>
                                    <div style={styles.featureItem}>
                                        <FaArrowRight style={styles.icon} />
                                        <span>Inspire others</span>
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
                
                <div className="mb-5"></div>
            </div>
        </>
    );
};

export default Feedback;
